/* eslint-disable react-refresh/only-export-components */
import { Save } from "lucide-react";
import type { RaRecord } from "ra-core";
import {
  Form,
  RecordContextProvider,
  ResourceContextProvider,
  useDataProvider,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import {
  Component,
  useCallback,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toSlug } from "@/lib/toSlug";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import ImageEditorField from "../misc/ImageEditorField";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";

// Seções da página de Settings. Cada uma tem Save independente — uma falha
// numa seção não bloqueia o salvamento das demais. Editor de funis/etapas
// (pipelines + pipeline_stages) virá na Fase B; aqui exibimos placeholder.
// `id` casa com o atributo HTML id do <Card> de cada seção (alvo do
// scrollIntoView ao clicar no item da sidebar). `funis-section` foi
// escolhido em vez de `pipelines` para evitar qualquer colisão DOM com
// um eventual nó referenciando o resource `pipelines` em outras telas.
const SECTIONS = [
  { id: "branding", label: "crm.settings.sections.branding" },
  { id: "companies", label: "resources.companies.name" },
  { id: "deals", label: "resources.deals.name" },
  { id: "funis-section", label: "crm.settings.sections.pipelines" },
  { id: "notes", label: "resources.notes.name" },
  { id: "tasks", label: "resources.tasks.name" },
];

// Fallback estático com a estrutura canônica dos funis e etapas do CRM
// FaroMarket. Usado quando o useGetList em pipelines/pipeline_stages retorna
// vazio (RLS bloqueando, cache miss, ou falha intermitente do data provider)
// para garantir que a seção de Funis SEMPRE seja renderizada com conteúdo
// utilizável na Fase A — o editor real lê do banco normalmente quando disponível.
const PIPELINES_FALLBACK: ReadonlyArray<{
  slug: string;
  label: string;
  description: string;
  stages: ReadonlyArray<{ slug: string; label: string; flag?: "won" | "lost" }>;
}> = [
  {
    slug: "comercial_faromarket",
    label: "Comercial FaroMarket",
    description: "Conduz a venda consultiva da FaroMarket depois da triagem.",
    stages: [
      { slug: "lead_qualificado", label: "Lead qualificado" },
      { slug: "primeiro_contato_realizado", label: "Primeiro contato realizado" },
      { slug: "diagnostico", label: "Diagnóstico" },
      { slug: "proposta_em_elaboracao", label: "Proposta em elaboração" },
      { slug: "proposta_enviada", label: "Proposta enviada" },
      { slug: "negociacao", label: "Negociação" },
      { slug: "fechamento", label: "Fechamento", flag: "won" },
    ],
  },
  {
    slug: "triagem_contatos",
    label: "Triagem de Contatos",
    description:
      "Recebe toda entrada inicial do site antes de virar oportunidade comercial qualificada.",
    stages: [
      { slug: "novo_contato", label: "Novo contato" },
      { slug: "em_analise", label: "Em análise" },
      { slug: "aguardando_informacoes", label: "Aguardando informações" },
      { slug: "encaminhado_comercial", label: "Encaminhado ao comercial", flag: "won" },
      { slug: "arquivado_sem_fit", label: "Arquivado / Sem fit", flag: "lost" },
      { slug: "descartado", label: "Descartado", flag: "lost" },
    ],
  },
];

/** Garante slug em cada item via toSlug(label) quando value vier vazio. */
const ensureValues = <T extends { value?: string; label: string }>(
  items: T[] | undefined,
): T[] | undefined =>
  items?.map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Valida que nenhum item em uso (referenciado em deals.<fieldName>) foi removido,
 * e que não há slugs duplicados. Continua exportada para preservar os testes
 * em SettingsPage.test.ts e o uso em categorias.
 *
 * IMPORTANTE: a partir da Fase A, esta função NÃO é mais usada para validar
 * `dealStages` — a fonte de verdade dos stages é public.pipeline_stages.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["pt-BR"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

// ---------------------------------------------------------------------------
// Hook compartilhado: persistência por seção
// ---------------------------------------------------------------------------
// Cada seção mescla apenas suas chaves no config global e persiste via
// dataProvider.update("configuration", { id: 1, … }). Isso garante:
//   - uma seção NUNCA sobrescreve dados de outra;
//   - se a validação local falhar, as demais seções continuam salváveis;
//   - localStorage é atualizado em paralelo (useConfigurationUpdater) para
//     refletir a mudança sem precisar refetch.
const useSaveConfigurationSection = () => {
  const dataProvider = useDataProvider();
  const updateConfiguration = useConfigurationUpdater();
  const config = useConfigurationContext();
  const notify = useNotify();

  return useCallback(
    async (patch: Partial<ConfigurationContextValue>) => {
      const merged: ConfigurationContextValue = { ...config, ...patch };
      try {
        await dataProvider.update("configuration", {
          id: 1,
          data: { config: merged },
          previousData: { id: 1, config },
        });
        updateConfiguration(merged);
        notify("crm.settings.saved", { type: "info" });
      } catch (error) {
        notify("crm.settings.save_error", { type: "error" });
        throw error;
      }
    },
    [config, dataProvider, notify, updateConfiguration],
  );
};

// ---------------------------------------------------------------------------
// Layout raiz
// ---------------------------------------------------------------------------
//
// IMPORTANTE — contexto do React Admin
// ------------------------------------
// Componentes como `<ArrayInput>` + `<SimpleFormIterator>` (admin/) chamam
// `useResourceContext()` e `useRecordContext()` internamente e fazem THROW
// quando esses contextos estão ausentes. A versão original da Fase A usava
// `<EditBase resource="configuration" id={1}>` que provia ambos. Ao quebrar
// o formulário monolítico em 5 seções independentes (cada uma com seu
// próprio `<Form>`), o contexto desapareceu — porque `<Form>` por si só
// não provê ResourceContext nem RecordContext globais.
//
// Esse era exatamente o crash em produção:
//     "SimpleFormIteratorItem must be used in a ResourceContextProvider
//      or be passed a resource prop."
//
// A correção mínima é envolver a página inteira em:
//   - ResourceContextProvider value="configuration"  → satisfaz useResourceContext
//   - RecordContextProvider value={config}            → satisfaz useRecordContext
//
// Como `configuration` é singleton (id=1) e cada seção mescla seu patch no
// objeto completo via useSaveConfigurationSection, prover o config atual
// como record é semanticamente correto: o "registro sendo editado" é
// exatamente o config.
export const SettingsPage = () => {
  const translate = useTranslate();
  const config = useConfigurationContext();

  return (
    <ResourceContextProvider value="configuration">
      <RecordContextProvider value={config}>
        <div className="flex gap-8 mt-4 pb-12">
          {/* Navegação lateral */}
          <nav className="hidden md:block w-48 shrink-0">
            <div className="sticky top-4 space-y-1">
              <h1 className="text-2xl font-semibold px-3 mb-2">
                {translate("crm.settings.title")}
              </h1>
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => {
                    document
                      .getElementById(section.id)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors"
                >
                  {translate(section.label, { smart_count: 2 })}
                </button>
              ))}
            </div>
          </nav>

          {/* Conteúdo */}
          <div className="flex-1 min-w-0 max-w-2xl space-y-6">
            <BrandingSection />
            <CompaniesSection />
            <DealsSection />
            <PipelinesPlaceholderSection />
            <NotesSection />
            <TasksSection />
          </div>
        </div>
      </RecordContextProvider>
    </ResourceContextProvider>
  );
};

SettingsPage.path = "/settings";

// ---------------------------------------------------------------------------
// Wrapper visual + Save por seção
// ---------------------------------------------------------------------------
const SectionCard = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) => (
  <Card id={id}>
    <CardContent className="space-y-4">
      <h2 className="text-xl font-semibold text-muted-foreground">{title}</h2>
      {children}
    </CardContent>
  </Card>
);

const SectionFooter = ({ isSubmitting }: { isSubmitting: boolean }) => {
  const translate = useTranslate();
  return (
    <div className="flex justify-end pt-2">
      <Button type="submit" disabled={isSubmitting}>
        <Save className="h-4 w-4 mr-1" />
        {isSubmitting
          ? translate("crm.settings.saving")
          : translate("ra.action.save")}
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Seção 1 — Identidade visual (título + logos)
// ---------------------------------------------------------------------------
const BrandingSection = () => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const save = useSaveConfigurationSection();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
    }),
    [config.title, config.lightModeLogo, config.darkModeLogo],
  );

  const onSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      await save({
        title: data.title,
        lightModeLogo:
          typeof data.lightModeLogo === "object"
            ? data.lightModeLogo?.src
            : data.lightModeLogo,
        darkModeLogo:
          typeof data.darkModeLogo === "object"
            ? data.darkModeLogo?.src
            : data.darkModeLogo,
      });
    } catch {
      // notify já disparou no hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} defaultValues={defaultValues}>
      <SectionCard
        id="branding"
        title={translate("crm.settings.sections.branding")}
      >
        <TextInput source="title" label="crm.settings.app_title" />
        <div className="flex gap-8">
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.light_mode_logo")}
            </p>
            <ImageEditorField
              source="lightModeLogo"
              width={100}
              height={100}
              linkPosition="bottom"
              backgroundImageColor="#f5f5f5"
            />
          </div>
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.dark_mode_logo")}
            </p>
            <ImageEditorField
              source="darkModeLogo"
              width={100}
              height={100}
              linkPosition="bottom"
              backgroundImageColor="#1a1a1a"
            />
          </div>
        </div>
        <SectionFooter isSubmitting={isSubmitting} />
      </SectionCard>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Seção 2 — Empresas (setores)
// ---------------------------------------------------------------------------
const CompaniesSection = () => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const save = useSaveConfigurationSection();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(
    () => ({ companySectors: config.companySectors }),
    [config.companySectors],
  );

  const onSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      await save({ companySectors: ensureValues(data.companySectors) });
    } catch {
      // notify já disparou
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} defaultValues={defaultValues}>
      <SectionCard
        id="companies"
        title={translate("resources.companies.name", { smart_count: 2 })}
      >
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("crm.settings.companies.sectors")}
        </h3>
        <ArrayInput source="companySectors" label={false} helperText={false}>
          <SimpleFormIterator disableReordering disableClear>
            <TextInput source="label" label={false} />
          </SimpleFormIterator>
        </ArrayInput>
        <SectionFooter isSubmitting={isSubmitting} />
      </SectionCard>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Seção 3 — Oportunidades (moeda + categorias)
// ---------------------------------------------------------------------------
// NÃO inclui Etapas: a fonte de verdade dos stages mudou para
// public.pipeline_stages. O editor de funis vem na Fase B (PipelinesPlaceholderSection).
const DealsSection = () => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const save = useSaveConfigurationSection();
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: deals } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const categoryDisplayName = translate(
    "crm.settings.validation.entities.categories",
  );
  const validateDealCategories = useCallback(
    (categories: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(categories, deals, "category", categoryDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
        inUse: (displayName, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: displayName,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [categoryDisplayName, deals, translate],
  );

  const defaultValues = useMemo(
    () => ({
      currency: config.currency,
      dealCategories: config.dealCategories,
    }),
    [config.currency, config.dealCategories],
  );

  const onSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      await save({
        currency: data.currency,
        dealCategories: ensureValues(data.dealCategories),
      });
    } catch {
      // notify já disparou
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} defaultValues={defaultValues}>
      <SectionCard
        id="deals"
        title={translate("resources.deals.name", { smart_count: 2 })}
      >
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("crm.settings.deals.currency")}
        </h3>
        <AutocompleteInput
          source="currency"
          label={false}
          choices={currencyChoices}
          inputText={(choice) => choice?.id}
          modal
        />

        <Separator />

        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("crm.settings.deals.categories")}
        </h3>
        <ArrayInput
          source="dealCategories"
          label={false}
          helperText={false}
          validate={validateDealCategories}
        >
          <SimpleFormIterator disableReordering disableClear>
            <TextInput source="label" label={false} />
          </SimpleFormIterator>
        </ArrayInput>

        <SectionFooter isSubmitting={isSubmitting} />
      </SectionCard>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Seção 4 — Funis de oportunidade (readonly Fase A)
// ---------------------------------------------------------------------------
// Editor CRUD completo (label, sort_order, is_active, is_won, is_lost) sobre
// public.pipelines + public.pipeline_stages será entregue na Fase B. Aqui na
// Fase A garantimos que o admin enxerga a estrutura real de funis, sem
// possibilidade de edição.
//
// Defesa em profundidade contra UI vazia:
//   1. PipelinesSectionErrorBoundary cobre crashes de renderização do
//      componente filho (ex.: erro inesperado em useGetList).
//   2. PipelinesSectionContent tenta o useGetList; quando o resultado vem
//      vazio ou indefinido (RLS bloqueando, cache miss, intermitência), cai
//      para PIPELINES_FALLBACK (lista estática canônica) — assim a seção
//      NUNCA desaparece da tela na Fase A.
//   3. Badge "Somente leitura" sempre presente, independentemente da fonte.
class PipelinesSectionErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log local apenas — o boundary NÃO esconde a seção, apenas troca o conteúdo.
    console.error("[Settings/Funis] render error", error, info);
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

const ReadOnlyBadge = () => {
  const translate = useTranslate();
  return (
    <span
      className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
      data-testid="funis-readonly-badge"
    >
      {translate("crm.settings.pipelines.readonly_badge")}
    </span>
  );
};

type PipelineRow = {
  slug: string;
  label: string;
  description?: string | null;
  stages: ReadonlyArray<{
    slug: string;
    label: string;
    flag?: "won" | "lost";
    sort_order?: number;
  }>;
};

const StageLine = ({
  index,
  stage,
}: {
  index: number;
  stage: PipelineRow["stages"][number];
}) => (
  <li className="flex items-center gap-2 text-muted-foreground">
    <span className="font-mono text-xs">#{stage.sort_order ?? index + 1}</span>
    <span>{stage.label}</span>
    {stage.flag === "won" ? (
      <span className="text-xs text-green-700">(ganho)</span>
    ) : null}
    {stage.flag === "lost" ? (
      <span className="text-xs text-red-700">(perdido)</span>
    ) : null}
  </li>
);

const PipelineBlock = ({ pipeline }: { pipeline: PipelineRow }) => (
  <div className="space-y-1">
    <h3 className="text-base font-medium">{pipeline.label}</h3>
    {pipeline.description ? (
      <p className="text-xs text-muted-foreground">{pipeline.description}</p>
    ) : null}
    <ul className="text-sm space-y-0.5">
      {pipeline.stages.map((stage, idx) => (
        <StageLine key={stage.slug} index={idx} stage={stage} />
      ))}
    </ul>
  </div>
);

const FallbackPipelineList = () => (
  <div className="space-y-4">
    {PIPELINES_FALLBACK.map((pipeline) => (
      <PipelineBlock key={pipeline.slug} pipeline={pipeline} />
    ))}
  </div>
);

const PipelinesSectionContent = () => {
  const translate = useTranslate();
  const { data: pipelines, isPending: pipelinesPending } = useGetList(
    "pipelines",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "sort_order", order: "ASC" },
      filter: { is_active: true },
    },
  );
  const { data: stages, isPending: stagesPending } = useGetList(
    "pipeline_stages",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "sort_order", order: "ASC" },
    },
  );

  if (pipelinesPending || stagesPending) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("crm.settings.pipelines.loading")}
      </p>
    );
  }

  const hasLiveData =
    pipelines !== undefined && pipelines.length > 0 && stages !== undefined;

  if (!hasLiveData) {
    return (
      <>
        <p className="text-xs text-muted-foreground italic">
          {translate("crm.settings.pipelines.empty_fallback")}
        </p>
        <FallbackPipelineList />
      </>
    );
  }

  return (
    <div className="space-y-4">
      {pipelines.map((pipeline) => {
        const pipelineStages =
          stages?.filter((s) => s.pipeline_id === pipeline.id) ?? [];
        const row: PipelineRow = {
          slug: pipeline.slug ?? String(pipeline.id),
          label: pipeline.label,
          description: pipeline.description,
          stages: pipelineStages.map((s) => ({
            slug: s.slug ?? String(s.id),
            label: s.label,
            flag: s.is_won ? "won" : s.is_lost ? "lost" : undefined,
            sort_order: s.sort_order,
          })),
        };
        return <PipelineBlock key={pipeline.id} pipeline={row} />;
      })}
    </div>
  );
};

const PipelinesPlaceholderSection = () => {
  const translate = useTranslate();
  return (
    <SectionCard
      id="funis-section"
      title={translate("crm.settings.sections.pipelines")}
    >
      <div className="flex items-center gap-2">
        <ReadOnlyBadge />
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.pipelines.intro")}
        </p>
      </div>
      <PipelinesSectionErrorBoundary fallback={<FallbackPipelineList />}>
        <PipelinesSectionContent />
      </PipelinesSectionErrorBoundary>
      <p className="text-xs text-muted-foreground">
        {translate("crm.settings.pipelines.editor_coming_soon")}
      </p>
    </SectionCard>
  );
};

// ---------------------------------------------------------------------------
// Seção 5 — Anotações (status de anotação)
// ---------------------------------------------------------------------------
const NotesSection = () => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const save = useSaveConfigurationSection();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(
    () => ({ noteStatuses: config.noteStatuses }),
    [config.noteStatuses],
  );

  const onSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      await save({ noteStatuses: ensureValues(data.noteStatuses) });
    } catch {
      // notify já disparou
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} defaultValues={defaultValues}>
      <SectionCard
        id="notes"
        title={translate("resources.notes.name", { smart_count: 2 })}
      >
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("crm.settings.notes.statuses")}
        </h3>
        <ArrayInput source="noteStatuses" label={false} helperText={false}>
          <SimpleFormIterator inline disableReordering disableClear>
            <TextInput source="label" label={false} className="flex-1" />
            <ColorInput source="color" />
          </SimpleFormIterator>
        </ArrayInput>
        <SectionFooter isSubmitting={isSubmitting} />
      </SectionCard>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Seção 6 — Tarefas (tipos de tarefa)
// ---------------------------------------------------------------------------
const TasksSection = () => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const save = useSaveConfigurationSection();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(
    () => ({ taskTypes: config.taskTypes }),
    [config.taskTypes],
  );

  const onSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      await save({ taskTypes: ensureValues(data.taskTypes) });
    } catch {
      // notify já disparou
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} defaultValues={defaultValues}>
      <SectionCard
        id="tasks"
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("crm.settings.tasks.types")}
        </h3>
        <ArrayInput source="taskTypes" label={false} helperText={false}>
          <SimpleFormIterator disableReordering disableClear>
            <TextInput source="label" label={false} />
          </SimpleFormIterator>
        </ArrayInput>
        <SectionFooter isSubmitting={isSubmitting} />
      </SectionCard>
    </Form>
  );
};

/** Color picker mínimo compatível com useInput de ra-core. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};
